import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppLoader } from '../components/shared/AppLoader.js';
import { Button } from '../components/design-system/primitives/Button/Button.js';
import { Card } from '../components/design-system/primitives/Card/Card.js';
import { SEO } from '../components/seo/index.js';
import { firebaseApplyEmailVerification, getFirebaseAuth } from '../lib/firebase.js';
import { useAuthStore } from '../store/authStore.js';

type ActionState = 'loading' | 'success' | 'error';

function resolveContinuePath(continueUrl: string | null, fallback: string): string {
  if (!continueUrl) return fallback;
  try {
    const url = new URL(continueUrl);
    if (url.origin === window.location.origin) {
      return `${url.pathname}${url.search}${url.hash}`;
    }
  } catch {
    /* ignore malformed continue URLs */
  }
  return fallback;
}

function firebaseActionErrorMessage(err: unknown, t: (key: string) => string): string {
  const code =
    err && typeof err === 'object' && 'code' in err ? String((err as { code: string }).code) : '';
  if (code === 'auth/invalid-action-code' || code === 'auth/expired-action-code') {
    return t('auth.action.errors.expired');
  }
  if (code === 'auth/user-disabled') {
    return t('auth.action.errors.userDisabled');
  }
  return t('auth.action.errors.generic');
}

export function AuthActionPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const reloadAndSync = useAuthStore((s) => s.reloadAndSync);
  const [state, setState] = useState<ActionState>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const mode = searchParams.get('mode');
      const oobCode = searchParams.get('oobCode');
      const continueUrl = searchParams.get('continueUrl');

      if (!mode || !oobCode) {
        if (!cancelled) {
          setErrorMessage(t('auth.action.errors.missingParams'));
          setState('error');
        }
        return;
      }

      try {
        if (mode === 'verifyEmail') {
          await firebaseApplyEmailVerification(oobCode);
          if (cancelled) return;

          const signedIn = Boolean(getFirebaseAuth().currentUser);
          if (signedIn) {
            await reloadAndSync();
            navigate(resolveContinuePath(continueUrl, '/welcome'), { replace: true });
            return;
          }

          navigate(resolveContinuePath(continueUrl, '/login?verified=1'), { replace: true });
          return;
        }

        if (mode === 'resetPassword') {
          const next = new URLSearchParams({ mode, oobCode });
          if (continueUrl) next.set('continueUrl', continueUrl);
          navigate(`/login?${next.toString()}`, { replace: true });
          return;
        }

        if (!cancelled) {
          setErrorMessage(t('auth.action.errors.unsupportedMode'));
          setState('error');
        }
      } catch (err) {
        if (!cancelled) {
          setErrorMessage(firebaseActionErrorMessage(err, t));
          setState('error');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [navigate, reloadAndSync, searchParams, t]);

  return (
    <>
      <SEO
        title={t('auth.action.title')}
        description={t('auth.action.metaDescription')}
      />
      <div className="flex min-h-screen items-center justify-center bg-ig-bg-primary px-6 py-12">
        <Card variant="outlined" className="w-full max-w-md rounded-lg !border-0 bg-ig-bg-primary p-6 md:p-8">
          {state === 'loading' ? (
            <div className="flex flex-col items-center gap-4 py-8 text-center">
              <AppLoader size="sm" className="min-h-0" />
              <p className="text-[14px] text-ig-text-secondary">{t('auth.action.processing')}</p>
            </div>
          ) : null}

          {state === 'error' ? (
            <div className="space-y-4 text-center">
              <h1 className="font-display text-[24px] font-normal text-ig-text-primary">{t('auth.action.errorTitle')}</h1>
              <p className="text-[14px] leading-relaxed text-ig-text-secondary">{errorMessage}</p>
              <Button
                type="button"
                variant="primary"
                size="lg"
                className="w-full rounded-md"
                onClick={() => navigate('/login', { replace: true })}
              >
                {t('auth.action.backToLogin')}
              </Button>
            </div>
          ) : null}
        </Card>
      </div>
    </>
  );
}
